import type { BarcodeWidthPolicy, QrErrorCorrection } from "../types/template";

export function cloneWidthPolicy(
  widthPolicy: BarcodeWidthPolicy,
): BarcodeWidthPolicy {
  return widthPolicy.mode === "fixed-module"
    ? { mode: "fixed-module", moduleWidthMm: widthPolicy.moduleWidthMm }
    : {
        mode: "fit-inside",
        minModuleDots: widthPolicy.minModuleDots,
        maxModuleDots: widthPolicy.maxModuleDots,
      };
}

export function canUseCode128SubsetC(value: string): boolean {
  return /^\d+$/.test(value) && value.length >= 2 && value.length % 2 === 0;
}

/**
 * Estimates the total number of Code 128 barcode modules for a given string.
 *
 * Default (set B) formula: `charCount * 11 + 35`
 * - 11 modules per character
 * - 35 overhead: start symbol (11) + checksum (11) + stop symbol (13)
 *
 * When `optimizeNumeric` is enabled and the value is all-digits with an even
 * number of characters, subset C is used and each *pair* of digits consumes
 * one symbol:
 *
 * subset C formula: `(digitPairCount * 11) + 35`
 */
export function estimateCode128Modules(
  value: string,
  optimizeNumeric = false,
): number {
  if (optimizeNumeric && canUseCode128SubsetC(value)) {
    const digitPairCount = Math.max(1, value.length / 2);
    return digitPairCount * 11 + 35;
  }

  const charCount = Math.max(1, value.length);
  return charCount * 11 + 35;
}

// QR alphanumeric capacity per version, indexed by error correction level.
// Covers versions 1–10 which handle up to ~174 alphanumeric chars at H level.
const QR_ALPHANUMERIC_CAPACITY: Record<QrErrorCorrection, number[]> = {
  L: [25, 47, 77, 114, 154, 195, 224, 279, 335, 395],
  M: [20, 38, 61, 90, 122, 154, 178, 221, 262, 311],
  Q: [16, 29, 47, 67, 87, 108, 125, 157, 189, 221],
  H: [10, 20, 35, 50, 64, 84, 93, 122, 143, 174],
};

/**
 * Estimates the minimum QR version (1–10) needed for the given data length
 * and error correction level. Uses alphanumeric mode capacity.
 */
export function estimateQrVersion(
  dataLength: number,
  errorCorrection: QrErrorCorrection,
): number {
  const capacity = QR_ALPHANUMERIC_CAPACITY[errorCorrection];
  for (let i = 0; i < capacity.length; i++) {
    const maxCapacity = capacity[i];
    if (maxCapacity !== undefined && dataLength <= maxCapacity) {
      return i + 1;
    }
  }
  return capacity.length;
}

/**
 * Returns the number of modules per side for a QR code version.
 * Version 1 = 21×21, version 2 = 25×25, ..., version N = (17 + 4*N)×(17 + 4*N).
 */
export function qrModulesPerSide(version: number): number {
  return 17 + version * 4;
}
